import React, {JSX} from 'react';
import styled, {IStyledComponent} from 'styled-components/native';
import RadioItem from './radioItem';
import {RadioPickerProps} from '../../interfaces/components/radioPicker';
import Label from '../label';

const Container: IStyledComponent<'native', any> = styled.View`
  width: 100%;
  flex-direction: ${({layout}) => layout};
  z-index: 99;
`;

/**
 * The `RadioPicker` component is designed to present a group of radio buttons allowing the user to make a single selection from multiple options. It encapsulates the functionality of individual `RadioItem` components, managing their selection state and providing a unified interface for receiving callbacks when the selection changes. This component is ideal for forms, settings, surveys, and anywhere a choice needs to be made from a list of options.
 *
 * @component
 *
 * @param {RadioPickerProps} props - The properties passed to the RadioPicker component.
 * @param {ViewStyle} props.style - Custom styles applied to the RadioPicker's container.
 * @param {string} props.accessibilityLabel - Accessibility label for the radio group, enhancing its accessibility for screen readers and assistive technologies.
 * @param {TextStyle} props.labelStyle - Custom styles that can be applied to each radio item's label. If an individual radio button specifies its own labelStyle, that style will take precedence over this prop.
 * @param {'row' | 'column'} props.layout - Determines the layout orientation of the radio buttons within the picker, either in a row or column. This affects the visual arrangement of the radio items.
 * @param {Function} props.onPress - Callback function invoked when any radio item within the group is selected, receiving the `id` of the selected item as an argument. This allows for centralized handling of radio selection changes.
 * @param {Array<RadioItemProps>} props.radioButtons - An array of properties for each radio button within the picker. Each item in the array corresponds to a `RadioItem` component, allowing individual configuration.
 * @param {string} props.selectedId - The `id` of the currently selected radio button. Matching one of the `id` values in the `radioButtons` array, this prop controls which radio item is visually marked as selected.
 *
 * @returns {JSX.Element} A cohesive component that renders a group of interactive radio items, providing feedback on selection and allowing users to choose a single option from the group.
 */
const RadioPicker: React.FC<RadioPickerProps> = ({
  style = {},
  accessibilityLabel,
  labelStyle,
  layout = 'column',
  onPress,
  radioButtons,
  selectedId,
  label,
  itemLabelStyle,
  isRequired,
}: RadioPickerProps): JSX.Element => {
  const handlePress = (id: string) => {
    if (id !== selectedId) {
      if (onPress) {
        onPress(id);
      }
      const button = radioButtons.find(rb => rb.id === id);
      if (button?.onPress) {
        button.onPress(id);
      }
    }
  };
  return (
    <Container
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={'radiogroup'}
      layout={layout}
      style={style}>
      {label && (
        <Label
          isBold
          labelStyle={labelStyle}
          label={label}
          isRequired={isRequired}
        />
      )}
      {radioButtons.map(button => (
        <RadioItem
          {...button}
          key={button.id}
          labelStyle={button.labelStyle || itemLabelStyle}
          selected={button.id === selectedId}
          onPress={handlePress}
        />
      ))}
    </Container>
  );
};

export default RadioPicker;
